import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostBinding, HostListener, input, Output, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  BookingAvailabilityOption,
  ConversationMessage,
} from '../../../../core/src/lib/interface/conversation.interface';
import { MessageType } from '@vendasta/conversation';
import { ChatContainerComponent, ChatMessageStatus, GalaxyChatModule } from '@vendasta/galaxy/chat';
import { GalaxyChatComposerModule } from '@vendasta/galaxy/chat-composer';
import { SentMessage } from '@vendasta/galaxy/chat-composer/src/chat-composer.component';
import { GlxyMarkdownPipe, LinkyPipe } from '@vendasta/galaxy/pipes';
import { MessageGroup } from '../../models/message-group';
import { DefaultPosition } from '../../models/webchat-config';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { BookingAvailabilityComponent, ConversationPipesModule } from '../../../../ui/src';
import { mapMessageStatus } from '../../../../core/src/lib/message-utils';
import { WebchatStatusComponent } from '../webchat-status/webchat-status.component';
import { SentFromYouPipe } from '../../pipes/send-from-you.pipe';
import { toObservable } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';

const defaultAssistantName = 'Chat Receptionist';

@Component({
  selector: 'webchat-box',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatTooltipModule,
    GalaxyChatModule,
    GalaxyChatComposerModule,
    LinkyPipe,
    GlxyMarkdownPipe,
    GalaxyAvatarModule,
    BookingAvailabilityComponent,
    ConversationPipesModule,
    WebchatStatusComponent,
    SentFromYouPipe,
    TranslateModule,
  ],
  templateUrl: './webchat-box.component.html',
  styleUrls: ['./webchat-box.component.scss'],
})
export class WebchatBoxComponent {
  @HostBinding('class') readonly hostClass = 'chat-box';

  webchatColor = input<string>('');
  webchatTextColor = input<string>('');
  webchatAccentColor = input<string>('');
  webchatAccentTextColor = input<string>('');
  welcomeMessage = input<string>('');
  assistantName = input(defaultAssistantName, {
    transform: (value: string | undefined) => value || defaultAssistantName,
  });
  assistantAvatarUrl = input<string>('');
  currentParticipantId = input.required<string | null>();
  messageGroups = input.required<MessageGroup[]>();
  messages = input.required<ConversationMessage[]>();
  messages$ = toObservable(this.messages);
  fullView = input<boolean>(false);
  position = input<'right' | 'left'>(DefaultPosition);
  absolutePosition = input<boolean>(true);
  isReplying = input(false);
  placeholder = input<string>('');
  showFooterContent = input<boolean>(false);
  poweredBy = input<{ name: string; url: string } | null>(null);
  processing = input<boolean>(false);

  @Output() clickedClose = new EventEmitter<void>();
  @Output() clickedHeader = new EventEmitter<void>();
  @Output() sentMessage = new EventEmitter<SentMessage>();
  @Output() setPreferredBooking = new EventEmitter<BookingAvailabilityOption>();

  @HostBinding('class.full-view') get fullViewStyles() {
    return this.fullView();
  }

  @HostBinding('class.mini-view') get miniViewStyles() {
    return !this.fullView();
  }

  @HostBinding('class.chat-box--right') get rightStyles() {
    return this.position() === 'right';
  }

  @HostBinding('class.chat-box--left') get leftStyles() {
    return this.position() === 'left';
  }

  @HostBinding('style.position') get positionStyles() {
    return this.absolutePosition() ? 'absolute' : 'relative';
  }

  @HostBinding('style.--primary-color') get stylePrimaryColor() {
    return this.webchatColor();
  }

  @HostBinding('style.--text-color') get styleTextColor() {
    if (this.webchatTextColor() === 'light') return 'white';
    return 'black';
  }

  @HostBinding('style.--custom-chat-bubble-color')
  @HostBinding('style.--custom-chat-button-color')
  get styleChatColor() {
    return this.webchatAccentColor();
  }

  @HostBinding('style.--custom-chat-bubble-text-color')
  @HostBinding('style.--custom-chat-button-text-color')
  get styleChatTextColor() {
    return this.webchatAccentTextColor() === 'dark' ? 'black' : 'white';
  }

  @ViewChild('chatContainer') chatContainer?: ChatContainerComponent;
  maxMessageLength = 1000;

  protected async sendMessage(msg: SentMessage): Promise<void> {
    this.sentMessage.emit(msg);
  }

  protected onClickClose(): void {
    this.clickedClose.emit();
  }

  protected onClickHeader(): void {
    this.clickedHeader.emit();
  }

  trackById(_: number, item: ConversationMessage | MessageGroup): string {
    if ('messageId' in item) return item.messageId || '';
    return item.id || '';
  }

  handleInput(event: KeyboardEvent): void {
    event.stopPropagation();
  }

  //WARP-605 - Preserve scroll on wheel event for Divi theme and websitepro
  @HostListener('wheel', ['$event'])
  onWheel(event: WheelEvent) {
    event.stopPropagation();
  }

  mapMessageStatus(message: ConversationMessage): ChatMessageStatus {
    return mapMessageStatus(message);
  }

  async handleSetPreferredBooking(booking: BookingAvailabilityOption) {
    this.setPreferredBooking.emit(booking);
  }

  protected readonly MessageType = MessageType;
}
